/**
 * useAgenda - Hook for agenda data fetching and inline editing
 * Feature 009: Workshop Booking & Job-Tracking System
 *
 * T039: Agenda hook for fetching and editing bookings
 */

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { AgendaBooking } from '../components/agenda/AgendaTable';
import { VersionConflictError } from './useInlineEdit';

/**
 * Bay data from API
 */
interface Bay {
  id: string;
  name: string;
  bayType: string;
}

/**
 * Agenda summary data
 */
interface AgendaSummary {
  date: string;
  summary: Record<string, number>;
  total: number;
}

/**
 * Options for useAgenda hook
 */
interface UseAgendaOptions {
  startDate: Date;
  endDate?: Date;
  bayId?: string;
  status?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

/**
 * Return type for useAgenda hook
 */
interface UseAgendaReturn {
  bookings: AgendaBooking[];
  bays: Bay[];
  summary: AgendaSummary | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  updateBooking: (
    bookingId: string,
    field: string,
    value: string,
    version: number
  ) => Promise<AgendaBooking>;
  isUpdating: boolean;
  updateError: Error | null;
}

/**
 * Hook for managing agenda data and inline editing
 */
export function useAgenda({
  startDate,
  endDate,
  bayId,
  status,
  sortBy = 'scheduledAt',
  sortDir = 'asc',
}: UseAgendaOptions): UseAgendaReturn {
  const queryClient = useQueryClient();
  const [updateError, setUpdateError] = useState<Error | null>(null);

  // Format dates for API (use local date, not UTC)
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const startDateStr = formatLocalDate(startDate);
  const endDateStr = endDate ? formatLocalDate(endDate) : undefined;

  // Build query key
  const queryKey = [
    'agenda',
    startDateStr,
    endDateStr,
    bayId,
    status,
    sortBy,
    sortDir,
  ].filter(Boolean);

  // Fetch bookings
  const {
    data: agendaData,
    isLoading: isLoadingBookings,
    isError: isBookingsError,
    error: bookingsError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('startDate', startDateStr);
      if (endDateStr) params.append('endDate', endDateStr);
      if (bayId) params.append('bayId', bayId);
      if (status) params.append('status', status);
      params.append('sortBy', sortBy);
      params.append('sortDir', sortDir);

      // api.get<T> returns T directly, throws ApiError on failure
      return await api.get<{
        bookings: AgendaBooking[];
        dateRange: { start: string; end: string };
        count: number;
      }>(`/api/agenda?${params.toString()}`);
    },
    staleTime: 30000, // Consider data stale after 30 seconds
    refetchInterval: 60000, // Refetch every minute
  });

  // Fetch bays
  const { data: baysData, isLoading: isLoadingBays } = useQuery({
    queryKey: ['agenda', 'bays'],
    queryFn: async () => {
      // api.get<T> returns T directly, throws ApiError on failure
      return await api.get<Bay[]>('/api/agenda/bays');
    },
    staleTime: 300000, // Bays rarely change, cache for 5 minutes
  });

  // Fetch summary
  const { data: summaryData } = useQuery({
    queryKey: ['agenda', 'summary', startDateStr],
    queryFn: async () => {
      // api.get<T> returns T directly, throws ApiError on failure
      return await api.get<AgendaSummary>(`/api/agenda/summary?date=${startDateStr}`);
    },
    staleTime: 30000,
  });

  // Update booking mutation
  const updateMutation = useMutation({
    mutationFn: async ({
      bookingId,
      field,
      value,
      version,
    }: {
      bookingId: string;
      field: string;
      value: string;
      version: number;
    }) => {
      // Map field names to API fields
      const updatePayload: Record<string, unknown> = {};

      switch (field) {
        case 'status':
          updatePayload.bookingStatus = value;
          break;
        case 'bay':
          updatePayload.bayId = value || null;
          break;
        case 'notes':
          updatePayload.notes = value || null;
          break;
        default:
          updatePayload[field] = value;
      }

      try {
        // api.patch<T> returns T directly, throws ApiError on failure
        return await api.patch<AgendaBooking>(`/api/agenda/${bookingId}`, updatePayload, {
          headers: {
            'If-Match': String(version),
          },
        });
      } catch (error) {
        // Check for version conflict (409)
        const apiError = error as { status?: number; details?: { currentVersion?: number } };
        if (apiError.status === 409) {
          throw new VersionConflictError(
            'Version conflict',
            apiError.details?.currentVersion || 0,
            version
          );
        }
        throw error;
      }
    },
    onSuccess: (updatedBooking) => {
      // Update the cache with the new booking data
      queryClient.setQueryData(queryKey, (old: typeof agendaData) => {
        if (!old) return old;
        return {
          ...old,
          bookings: old.bookings.map((b: AgendaBooking) =>
            b.id === updatedBooking.id ? updatedBooking : b
          ),
        };
      });
      setUpdateError(null);
    },
    onError: (error) => {
      setUpdateError(error as Error);
    },
  });

  // Update booking handler
  const updateBooking = useCallback(
    async (
      bookingId: string,
      field: string,
      value: string,
      version: number
    ): Promise<AgendaBooking> => {
      return updateMutation.mutateAsync({ bookingId, field, value, version });
    },
    [updateMutation]
  );

  return {
    bookings: agendaData?.bookings || [],
    bays: baysData || [],
    summary: summaryData || null,
    isLoading: isLoadingBookings || isLoadingBays,
    isError: isBookingsError,
    error: bookingsError as Error | null,
    refetch,
    updateBooking,
    isUpdating: updateMutation.isPending,
    updateError,
  };
}

/**
 * Hook for date navigation in agenda
 */
export function useAgendaNavigation(initialDate?: Date) {
  const [currentDate, setCurrentDate] = useState<Date>(initialDate || new Date());

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const goToPreviousDay = useCallback(() => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });
  }, []);

  const goToNextDay = useCallback(() => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 1);
      return newDate;
    });
  }, []);

  const goToDate = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  const isToday = useCallback(() => {
    const today = new Date();
    return (
      currentDate.getDate() === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  }, [currentDate]);

  return {
    currentDate,
    goToToday,
    goToPreviousDay,
    goToNextDay,
    goToDate,
    isToday,
  };
}
